import React, { useRef, useEffect, useState, useCallback } from 'react';
import { parseGIF, decompressFrames } from 'gifuct-js';
import biosFont from '../desktop/assets/fonts/Ac437_IBM_BIOS.ttf';

// CRT shader — vertex
const VERT_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_position.x * 0.5 + 0.5, 1.0 - (a_position.y * 0.5 + 0.5));
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// CRT shader — fragment
const FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_time;
uniform vec2 u_resolution;

vec2 barrelDistort(vec2 uv, float amt) {
  vec2 cc = uv - 0.5;
  float dist = dot(cc, cc);
  return uv + cc * dist * amt;
}

float scanline(float y, float res) {
  return 0.92 + 0.08 * sin(y * res * 3.14159 * 2.0);
}

vec3 shadowMask(vec2 fragCoord) {
  float x = mod(fragCoord.x, 3.0);
  vec3 mask = vec3(0.8);
  if (x < 1.0) mask.r = 1.0;
  else if (x < 2.0) mask.g = 1.0;
  else mask.b = 1.0;
  return mask;
}

void main() {
  vec2 uv = barrelDistort(v_uv, 0.15);
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  float aberr = 0.002;
  float r = texture2D(u_texture, vec2(uv.x + aberr, uv.y)).r;
  float g = texture2D(u_texture, uv).g;
  float b = texture2D(u_texture, vec2(uv.x - aberr, uv.y)).b;
  vec3 color = vec3(r, g, b);
  float scan = scanline(uv.y, u_resolution.y * 0.5);
  color *= scan;
  vec2 fragCoord = uv * u_resolution;
  color *= shadowMask(fragCoord);
  vec3 bloom = vec3(0.0);
  for (float i = -2.0; i <= 2.0; i += 1.0) {
    for (float j = -2.0; j <= 2.0; j += 1.0) {
      vec2 offset = vec2(i, j) / u_resolution;
      bloom += texture2D(u_texture, uv + offset).rgb;
    }
  }
  bloom /= 25.0;
  color += bloom * 0.08;
  vec2 vig = uv * (1.0 - uv);
  float vigAmount = vig.x * vig.y * 15.0;
  vigAmount = pow(vigAmount, 0.25);
  color *= vigAmount;
  float barPos = fract(u_time * 0.08);
  float barDist = abs(uv.y - barPos);
  float bar = smoothstep(0.06, 0.0, barDist) * 0.12;
  color += color * bar;
  color *= 0.98 + 0.02 * sin(u_time * 8.0);
  color *= vec3(1.0, 1.02, 0.98);
  color *= 1.3;
  gl_FragColor = vec4(color, 1.0);
}
`;

function createShaderProgram(gl) {
  const vs = gl.createShader(gl.VERTEX_SHADER);
  gl.shaderSource(vs, VERT_SRC);
  gl.compileShader(vs);
  if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
    console.error('Vertex shader:', gl.getShaderInfoLog(vs));
    return null;
  }
  const fs = gl.createShader(gl.FRAGMENT_SHADER);
  gl.shaderSource(fs, FRAG_SRC);
  gl.compileShader(fs);
  if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
    console.error('Fragment shader:', gl.getShaderInfoLog(fs));
    return null;
  }
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error('Link:', gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}

// --- Stations (YouTube live streams only — no ads via embed) ---
const STATIONS = [
  { name: 'THING FM', freq: '88.1', gif: '/pixel-1.gif', ytId: 'jfKfPfyJRdk' },  // lofi hip hop radio 📚
  { name: 'THING FM', freq: '91.4', gif: '/pixel-2.gif', ytId: '4xDzrJKXOOY' },  // synthwave radio 🌌
  { name: 'THING FM', freq: '95.7', gif: '/pixel-3.gif', ytId: 'HuFYqnbVbzY' },  // jazz lofi radio 🎷
  { name: 'THING FM', freq: '98.3', gif: '/pixel-1.gif', ytId: 'blAFxjhg62k' },  // Coffee Shop Radio ☕
  { name: 'THING FM', freq: '101.9', gif: '/pixel-2.gif', ytId: 'WGfMj5o4s1k' }, // Video Game Lofi 🎮
  { name: 'THING FM', freq: '104.6', gif: '/pixel-3.gif', ytId: 'E_m998odXqo' },  // Japanese Town Lofi 🎶
  { name: 'THING FM', freq: '106.2', gif: '/pixel-1.gif', ytId: '9kiqPcGAuj0' },  // 90's Chill Lofi ☕️
  { name: 'THING FM', freq: '108.8', gif: '/pixel-2.gif', ytId: '28KRPhVzCus' },  // lofi sleep/chill 💤
];

// Load YouTube IFrame API
function loadYTApi() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    const existing = document.getElementById('yt-iframe-api');
    if (existing) {
      const check = setInterval(() => {
        if (window.YT && window.YT.Player) { clearInterval(check); resolve(); }
      }, 100);
      return;
    }
    const tag = document.createElement('script');
    tag.id = 'yt-iframe-api';
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
}


const STATION_FONTS = [
  '"Silkscreen", monospace',
  '"Comic Sans MS", "Comic Sans", cursive',
  '"Papyrus", fantasy',
  '"Impact", "Arial Black", sans-serif',
  '"Georgia", "Times New Roman", serif',
  '"Trebuchet MS", "Lucida Grande", sans-serif',
  '"Courier New", "Courier", monospace',
  '"Lucida Console", "Monaco", monospace',
  '"Arial", "Helvetica", sans-serif',
  '"Palatino Linotype", "Book Antiqua", serif',
  '"Verdana", "Geneva", sans-serif',
  'fantasy',
];

const STANDBY_GIF_URL = '/standby.gif';

// --- Thing FM config ---
const THINGFM_BASE = (process.env.PUBLIC_URL || '') + '/thingfm';
const THINGFM_INTRO = THINGFM_BASE + '/shorty terrys mom.mp3';
const THINGFM_ADS = [
  'AD Angry Face Trailer', 'AD GOM GOM fountain', 'AD compensation',
  'AD compensation 2', 'AD compensation 3', 'AD dr shiela funny farm',
  'AD drawings from my one year old', 'AD eman calculator ',
  'AD gom gom burgers 2', 'AD gom gom burgers 3', 'AD gom gom cyber monday',
  'AD gom gom space water', 'AD gom goms burgers', 'AD hacienda 3',
  'AD hacienda 40 can beer_4', 'AD mints mcgee', 'AD scoop soldiers',
  'AD scrappys pickles 2', 'AD sisquo', 'AD the dillership',
  'AD thingaroni TERRY', 'AD thingsburg diamond', 'ad lonely husbands club',
].map(f => THINGFM_BASE + '/' + f + '.mp3');
const THINGFM_SHORTIES = [
  'SHORTY EDDY IS KONGZ', 'shorty bruce', 'shorty gom gom',
  'shorty mister Quazar', 'shorty terry', 'shorty terrys mom',
  'shorty whitelist', 'shorty zeneca',
].map(f => THINGFM_BASE + '/' + f + '.mp3');

const AD_BREAK_MIN = 1 * 60 * 1000;
const AD_BREAK_MAX = 3 * 60 * 1000;
const DEV_MODE = true;
const AD_VOL_SCALE = 0.65; // ads play at 65% of user volume to match YT music level

// --- Signal texts (bleed through on screen) ---
const SIGNAL_TEXTS = [
  'we found your frequency by accident. we were looking for someone else, but the signal bent toward you. it does that sometimes. the wavelengths have preferences.',
  'there is a room with no walls where all the lost radio signals end up. songs half-heard from passing cars. weather reports for cities that no longer exist. your voice, once, asking a question you have since forgotten.',
  'the thingdoms were here before the towers. before the wires. we broadcast on frequencies that taste like warm milk and sound like the colour of sleep. tune in. tune in. tune in.',
  'someone left a radio playing in an empty house and the songs kept going long after the power should have died. the neighbours say they can hear it through the walls. it plays songs that haven\'t been written yet.',
  'do not adjust your set. the static is not interference — it is the space between thoughts. if you listen closely you can hear the ocean. if you listen closer than that, the ocean hears you back.',
  'this transmission originates from a place that exists only when you are not looking directly at it. like a word on the tip of your tongue. like the feeling of remembering a dream while still inside it.',
  'we have been broadcasting since before the towers were built. the signal travels in circles, growing wider each time. one day it will be large enough to hold everything. until then, we wait between the channels.',
  'the frequency is shifting. can you feel it? like the air before a storm, but softer. like being remembered by someone you have never met.',
];

// Inject IBM BIOS font + CRT glow styles
const fontStyleId = 'crt-bios-font';
if (typeof document !== 'undefined' && !document.getElementById(fontStyleId)) {
  const style = document.createElement('style');
  style.id = fontStyleId;
  style.textContent = `
    @font-face {
      font-family: 'IBM BIOS';
      src: url(${biosFont}) format('truetype');
      font-weight: normal;
      font-style: normal;
    }
    .crt-glow {
      text-shadow:
        0 0 2px rgba(255,255,255,0.6),
        0 0 6px rgba(255,255,255,0.2);
      image-rendering: pixelated;
    }
    .crt-glow-warm {
      text-shadow:
        0 0 2px rgba(255,255,255,0.6),
        0 0 6px rgba(255,255,255,0.2);
    }
    .crt-btn {
      background: none;
      border: none;
      color: rgba(255,255,255,0.6);
      cursor: pointer;
      padding: 4px;
      font-family: 'IBM BIOS', 'Lucida Console', monospace;
      text-shadow:
        0 0 2px rgba(255,255,255,0.3);
      font-size: 16px;
      line-height: 1;
      image-rendering: pixelated;
    }
    .crt-btn:hover {
      color: #ffffff;
      text-shadow:
        0 0 2px rgba(255,255,255,0.8),
        0 0 10px rgba(255,255,255,0.3);
    }
    .crt-btn.active {
      color: #ffffff;
      text-shadow:
        0 0 2px rgba(255,255,255,0.8);
    }
    .vol-bar {
      display: inline-block;
      width: 3px;
      margin: 0 1.5px;
      background: rgba(255,255,255,0.12);
      vertical-align: bottom;
      border-radius: 0;
    }
    .vol-bar.lit {
      background: rgba(255,255,255,0.85);
      box-shadow: 0 0 3px rgba(255,255,255,0.5), 0 0 10px rgba(255,255,255,0.15);
    }
    @keyframes crt-flicker {
      0%, 100% { opacity: 0.92; }
      3% { opacity: 0.87; }
      6% { opacity: 0.95; }
      50% { opacity: 0.93; }
      53% { opacity: 0.88; }
      56% { opacity: 0.94; }
    }
    .crt-ui-overlay {
      animation: crt-flicker 4s infinite;
    }
  `;
  document.head.appendChild(style);
}

export default function CRTLab() {
  const imgCanvasRef = useRef(null);
  const glCanvasRef = useRef(null);

  const stationFramesRef = useRef({});
  const standbyFramesRef = useRef(null);
  const showStandbyRef = useRef(false);
  const showStaticRef = useRef(false);

  const [loaded, setLoaded] = useState(false);
  const [stationIndex, setStationIndex] = useState(0);
  const stationIndexRef = useRef(0);
  const [stationFont, setStationFont] = useState(STATION_FONTS[0]);
  const stationFontIdxRef = useRef(0);

  const [infoOpen, setInfoOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);

  // --- YouTube player ---
  const ytPlayerRef = useRef(null);
  const ytReadyRef = useRef(false);

  // --- Thing FM state ---
  const adRef = useRef(null);
  const adBreakTimerRef = useRef(null);
  const adPlayedRef = useRef([]);
  const introPlayedRef = useRef(false);
  const playingRef = useRef(false);
  const staticCtxRef = useRef(null);
  const mutedRef = useRef(false);
  const inAdBreakRef = useRef(false);

  const [radioPlaying, setRadioPlaying] = useState(false);
  const [radioMuted, setRadioMuted] = useState(false);
  const [radioStatus, setRadioStatus] = useState('off');
  const [volume, setVolume] = useState(1);
  const volumeRef = useRef(1);
  const [nowPlaying, setNowPlaying] = useState('');

  // --- Signal text state (drawn onto canvas, inside CRT effect) ---
  const signalTextRef = useRef(null); // current signal text string
  const signalOpacityRef = useRef(0); // 0-1 fade
  const signalTargetRef = useRef(0); // target opacity (0 = fading out, 0.85 = visible)
  const signalTimerRef = useRef(null);
  const signalFadeTimerRef = useRef(null);
  const signalCharRevealRef = useRef(0); // how many chars revealed so far
  const signalStartTimeRef = useRef(0);

  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // --- Auto-hide controls ---
  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  useEffect(() => {
    const onMove = () => showControls();
    window.addEventListener('mousemove', onMove);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 4000);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(hideTimerRef.current);
    };
  }, [showControls]);

  // --- Signal text scheduling ---
  const triggerSignal = useCallback(() => {
    const text = SIGNAL_TEXTS[Math.floor(Math.random() * SIGNAL_TEXTS.length)];
    // Duration: typing at 12 chars/sec + 2.5s hold per sentence + 2s buffer
    const sentences = text.match(/[^.!?]+[.!?]?\s*/g) || [text];
    const duration = sentences.reduce((t, s) => t + (s.length / 12) * 1000 + 2500, 0) + 2000;
    signalTextRef.current = text;
    signalCharRevealRef.current = 0;
    signalStartTimeRef.current = performance.now();

    clearTimeout(signalFadeTimerRef.current);
    signalFadeTimerRef.current = setTimeout(() => {
      signalTextRef.current = null;
    }, duration);
  }, []);

  useEffect(() => {
    if (!radioPlaying) {
      clearTimeout(signalTimerRef.current);
      return;
    }
    function scheduleNext() {
      const delay = 25000 + Math.random() * 35000;
      signalTimerRef.current = setTimeout(() => {
        triggerSignal();
        scheduleNext();
      }, delay);
    }
    signalTimerRef.current = setTimeout(() => {
      triggerSignal();
      scheduleNext();
    }, 10000 + Math.random() * 8000);
    return () => clearTimeout(signalTimerRef.current);
  }, [radioPlaying, triggerSignal]);

  // --- Init YouTube player + audio elements ---
  useEffect(() => {
    const ad = new Audio();
    ad.preload = 'none';
    adRef.current = ad;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const staticGain = audioCtx.createGain();
    staticGain.gain.value = 0;
    staticGain.connect(audioCtx.destination);
    const bufferSize = audioCtx.sampleRate * 2;
    const noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
    staticCtxRef.current = { audioCtx, staticGain, noiseBuffer };

    // Create YT player container OUTSIDE React to avoid re-render issues
    let ytDiv = document.getElementById('yt-player-crt');
    if (!ytDiv) {
      ytDiv = document.createElement('div');
      ytDiv.id = 'yt-player-crt';
      document.body.appendChild(ytDiv);
    }

    loadYTApi().then(() => {
      if (ytPlayerRef.current) return; // already created
      ytPlayerRef.current = new window.YT.Player('yt-player-crt', {
        videoId: STATIONS[0].ytId,
        playerVars: { controls: 0, playsinline: 1 },
        events: {
          onReady: () => {
            console.log('[YT] Player ready');
            ytReadyRef.current = true;
            ytPlayerRef.current.setVolume(0);
          },
          onError: (e) => console.error('[YT] Error:', e.data),
          onStateChange: (e) => {
            const states = { '-1': 'unstarted', 0: 'ended', 1: 'playing', 2: 'paused', 3: 'buffering', 5: 'cued' };
            console.log('[YT] State:', states[e.data] || e.data);
          },
        },
      });
    });

    return () => {
      ad.pause(); ad.src = '';
      clearTimeout(adBreakTimerRef.current);
      audioCtx.close();
      if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
        try { ytPlayerRef.current.destroy(); } catch (e) { /* */ }
        ytPlayerRef.current = null;
        ytReadyRef.current = false;
      }
      const el = document.getElementById('yt-player-crt');
      if (el) el.remove();
    };
  }, []);

  // Sync volume to YT + ad audio
  useEffect(() => {
    const yt = ytPlayerRef.current;
    if (radioMuted) {
      if (yt && ytReadyRef.current) try { yt.setVolume(0); } catch(e) {}
      if (adRef.current) adRef.current.volume = 0;
      return;
    }
    if (yt && ytReadyRef.current && radioPlaying && !inAdBreakRef.current) {
      try { yt.setVolume(Math.round(volume * 100)); } catch(e) {}
    }
    if (adRef.current && inAdBreakRef.current) {
      adRef.current.volume = volume * AD_VOL_SCALE;
    }
  }, [volume, radioPlaying, radioMuted]);

  function playStaticBurst(vol, durationMs, onDone) {
    const s = staticCtxRef.current;
    if (!s) { if (onDone) onDone(); return; }
    const ctx = s.audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const t = ctx.currentTime;
    const dur = durationMs / 1000;
    const noise = ctx.createBufferSource();
    noise.buffer = s.noiseBuffer;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(vol * 0.25, t);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.05, t + dur * 0.15);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.35, t + dur * 0.25);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.08, t + dur * 0.4);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.3, t + dur * 0.55);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.12, t + dur * 0.7);
    noiseGain.gain.linearRampToValueAtTime(vol * 0.2, t + dur * 0.85);
    noiseGain.gain.linearRampToValueAtTime(0, t + dur);
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.setValueAtTime(2000, t);
    bandpass.frequency.linearRampToValueAtTime(800, t + dur * 0.3);
    bandpass.frequency.linearRampToValueAtTime(3500, t + dur * 0.6);
    bandpass.frequency.linearRampToValueAtTime(1200, t + dur);
    bandpass.Q.value = 1.5;
    noise.connect(bandpass);
    bandpass.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    const ghost = ctx.createOscillator();
    ghost.type = 'sine';
    ghost.frequency.setValueAtTime(300 + Math.random() * 200, t);
    ghost.frequency.linearRampToValueAtTime(500 + Math.random() * 400, t + dur * 0.5);
    ghost.frequency.linearRampToValueAtTime(200 + Math.random() * 150, t + dur);
    const ghostGain = ctx.createGain();
    ghostGain.gain.setValueAtTime(0, t);
    ghostGain.gain.linearRampToValueAtTime(vol * 0.04, t + dur * 0.3);
    ghostGain.gain.linearRampToValueAtTime(0, t + dur * 0.5);
    ghostGain.gain.linearRampToValueAtTime(vol * 0.03, t + dur * 0.7);
    ghostGain.gain.linearRampToValueAtTime(0, t + dur);
    ghost.connect(ghostGain);
    ghostGain.connect(ctx.destination);
    noise.start(t);
    noise.stop(t + dur);
    ghost.start(t);
    ghost.stop(t + dur);
    setTimeout(() => { if (onDone) onDone(); }, durationMs);
  }

  const pickRandomClip = useCallback((list) => {
    const available = list.filter(f => !adPlayedRef.current.includes(f));
    const pool = available.length > 0 ? available : list;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    adPlayedRef.current.push(pick);
    if (adPlayedRef.current.length > Math.floor(list.length * 0.7)) {
      adPlayedRef.current = adPlayedRef.current.slice(-3);
    }
    return pick;
  }, []);

  const scheduleAdBreak = useCallback(() => {
    clearTimeout(adBreakTimerRef.current);
    const delay = AD_BREAK_MIN + Math.random() * (AD_BREAK_MAX - AD_BREAK_MIN);
    adBreakTimerRef.current = setTimeout(() => {
      if (playingRef.current) playAdBreak();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playAdBreak = useCallback(() => {
    const yt = ytPlayerRef.current;
    const ad = adRef.current;
    if (!yt || !ad || !playingRef.current) return;

    const clips = [];
    if (Math.random() > 0.4) clips.push(pickRandomClip(THINGFM_SHORTIES));
    clips.push(pickRandomClip(THINGFM_ADS));

    let clipIndex = 0;
    inAdBreakRef.current = true;
    showStandbyRef.current = true;
    setRadioStatus('ad break');
    setNowPlaying('AD BREAK');

    const isMuted = mutedRef.current;
    const targetVol = isMuted ? 0 : volumeRef.current;
    // Fade YT volume down
    let fadeVol = isMuted ? 0 : Math.round(volumeRef.current * 100);
    const fadeDown = setInterval(() => {
      fadeVol = Math.max(0, fadeVol - 10);
      try { yt.setVolume(fadeVol); } catch(e) {}
      if (fadeVol <= 0) {
        clearInterval(fadeDown);
        const staticAudio = new Audio(THINGFM_BASE + '/static.mp3');
        staticAudio.volume = targetVol;
        staticAudio.play().catch(e => console.error('[Audio] play failed:', e));
        staticAudio.onended = () => playNextClip();
      }
    }, 20);

    function playNextClip() {
      if (clipIndex >= clips.length) {
        const curVol = mutedRef.current ? 0 : volumeRef.current;
        const outroStatic = new Audio(THINGFM_BASE + '/static.mp3');
        outroStatic.volume = curVol;
        outroStatic.play().catch(e => console.error('[Audio] play failed:', e));
        outroStatic.onended = () => {
          inAdBreakRef.current = false;
          showStandbyRef.current = false;
          try { yt.setVolume(mutedRef.current ? 0 : Math.round(volumeRef.current * 100)); } catch(e) {}
          setRadioStatus('on air');
          setNowPlaying('THING FM');
          scheduleAdBreak();
        };
        return;
      }
      ad.src = clips[clipIndex];
      ad.volume = mutedRef.current ? 0 : volumeRef.current * AD_VOL_SCALE;
      console.log('[AD] Playing clip:', clips[clipIndex], 'vol:', ad.volume);
      ad.play().then(() => console.log('[AD] Clip playing OK')).catch(e => console.error('[AD] Clip play FAILED:', e));
      const name = decodeURIComponent(clips[clipIndex].split('/').pop()).replace('.mp3', '');
      setNowPlaying(name);
      clipIndex++;
      ad.onended = () => playNextClip();
    }
  }, [pickRandomClip, scheduleAdBreak]);

  function toggleRadio() {
    const yt = ytPlayerRef.current;
    const ad = adRef.current;
    if (!yt || !ytReadyRef.current) {
      console.log('[YT] Not ready yet, ytReady:', ytReadyRef.current);
      return;
    }

    if (radioPlaying) {
      const newMuted = !radioMuted;
      mutedRef.current = newMuted;
      setRadioMuted(newMuted);
      try {
        if (newMuted) { yt.mute(); }
        else { yt.unMute(); yt.setVolume(inAdBreakRef.current ? 0 : Math.round(volume * 100)); }
      } catch(e) {}
      if (ad) ad.volume = newMuted ? 0 : volume * AD_VOL_SCALE;
      return;
    }

    setRadioPlaying(true);
    playingRef.current = true;
    mutedRef.current = false;
    setRadioMuted(false);

    // MUST call playVideo() synchronously from user click for Chrome autoplay
    try {
      yt.unMute();
      yt.setVolume(0);
      yt.playVideo();
      console.log('[YT] playVideo() called, state:', yt.getPlayerState());
    } catch(e) { console.error('[YT] play failed:', e); }

    if (!introPlayedRef.current && ad) {
      introPlayedRef.current = true;
      inAdBreakRef.current = true; // prevent volume sync from unmuting YT during intro
      showStandbyRef.current = true;
      setRadioStatus('intro');
      setNowPlaying('TUNING IN...');
      ad.src = THINGFM_INTRO;
      ad.volume = volume * AD_VOL_SCALE;
      console.log('[AD] Playing intro, vol:', ad.volume, 'src:', ad.src);
      ad.play().then(() => console.log('[AD] Intro playing OK')).catch(e => console.error('[AD] Intro play FAILED:', e));
      ad.onended = () => {
        const introStatic = new Audio(THINGFM_BASE + '/static.mp3');
        introStatic.volume = volume;
        introStatic.play().catch(e => console.error('[Audio] play failed:', e));
        introStatic.onended = () => {
          inAdBreakRef.current = false;
          showStandbyRef.current = false;
          try {
            yt.unMute();
            yt.setVolume(Math.round(volume * 100));
            console.log('[YT] Intro done, unmuted, vol:', yt.getVolume(), 'state:', yt.getPlayerState(), 'muted:', yt.isMuted());
          } catch(e) {}
          setRadioStatus('on air');
          setNowPlaying('THING FM');
          scheduleAdBreak();
        };
      };
    } else {
      try { yt.unMute(); yt.setVolume(Math.round(volume * 100)); } catch(e) {}
      setRadioStatus('on air');
      setNowPlaying('THING FM');
      scheduleAdBreak();
    }
  }

  // --- Station switching ---
  const changingStationRef = useRef(false);
  const changeStation = useCallback((direction) => {
    if (changingStationRef.current) return; // prevent rapid switching during transition
    changingStationRef.current = true;

    const yt = ytPlayerRef.current;
    const isMuted = mutedRef.current;
    const vol = isMuted ? 0 : volumeRef.current;

    // 1. Mute YT immediately
    if (yt && ytReadyRef.current) {
      try { yt.setVolume(0); } catch(e) {}
    }

    // 2. Show visual static
    showStaticRef.current = true;

    // 3. Play static.mp3 audio
    const staticAudio = new Audio(THINGFM_BASE + '/static.mp3');
    staticAudio.volume = vol;
    staticAudio.play().catch(e => console.error('[Audio] play failed:', e));

    staticAudio.onended = () => {
      // 4. Switch station
      setStationIndex(prev => {
        const next = (prev + direction + STATIONS.length) % STATIONS.length;
        stationIndexRef.current = next;
        if (yt && ytReadyRef.current && playingRef.current) {
          try { yt.loadVideoById(STATIONS[next].ytId); } catch(e) {}
        }
        return next;
      });

      // 5. Cycle font
      stationFontIdxRef.current = (stationFontIdxRef.current + (direction > 0 ? 1 : STATION_FONTS.length - 1)) % STATION_FONTS.length;
      setStationFont(STATION_FONTS[stationFontIdxRef.current]);

      // 6. Hide visual static, restore YT volume
      showStaticRef.current = false;
      if (yt && ytReadyRef.current && !isMuted) {
        try { yt.setVolume(Math.round(volumeRef.current * 100)); } catch(e) {}
      }
      changingStationRef.current = false;
    };

    showControls();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showControls]);

  const changeGif = useCallback(() => {
    showControls();
  }, [showControls]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); changeStation(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); changeStation(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setVolume(v => Math.min(1, v + 0.05)); showControls(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setVolume(v => Math.max(0, v - 0.05)); showControls(); }
      else if (e.key === ' ') { e.preventDefault(); toggleRadio(); showControls(); }
      else if (e.key === 'Enter') { e.preventDefault(); changeGif(); }
      else if (e.key === 'i' || e.key === 'I') { setInfoOpen(o => !o); showControls(); }
      else if (e.key === 'd' || e.key === 'D') { if (DEV_MODE) playAdBreak(); showControls(); }
      else if (e.key === 's' || e.key === 'S') { if (DEV_MODE) triggerSignal(); showControls(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeStation, changeGif, showControls, triggerSignal]);

  // --- GIF loading ---
  const decodeGif = useCallback(async (url) => {
    const resp = await fetch(url);
    const buff = await resp.arrayBuffer();
    const gif = parseGIF(buff);
    const frames = decompressFrames(gif, true);
    if (frames.length === 0) return null;
    const gifW = frames[0].dims.width;
    const gifH = frames[0].dims.height;
    const compCanvas = document.createElement('canvas');
    compCanvas.width = gifW;
    compCanvas.height = gifH;
    const compCtx = compCanvas.getContext('2d');
    const composited = [];
    for (const frame of frames) {
      const patch = new ImageData(new Uint8ClampedArray(frame.patch), frame.dims.width, frame.dims.height);
      if (frame.disposalType === 2) compCtx.clearRect(0, 0, gifW, gifH);
      const patchCanvas = document.createElement('canvas');
      patchCanvas.width = frame.dims.width;
      patchCanvas.height = frame.dims.height;
      patchCanvas.getContext('2d').putImageData(patch, 0, 0);
      compCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);
      const snapshot = compCtx.getImageData(0, 0, gifW, gifH);
      composited.push({ imageData: snapshot, delay: frame.delay || 100, width: gifW, height: gifH });
    }
    return composited;
  }, []);

  useEffect(() => {
    const canvas = imgCanvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    let cancelled = false;
    async function loadAll() {
      try {
        const promises = STATIONS.map((s, i) => decodeGif(s.gif).then(frames => ({ index: i, frames })));
        promises.push(decodeGif(STANDBY_GIF_URL).then(frames => ({ index: 'standby', frames })).catch(() => null));
        const results = await Promise.all(promises);
        if (cancelled) return;
        for (const r of results) {
          if (!r || !r.frames) continue;
          if (r.index === 'standby') standbyFramesRef.current = r.frames;
          else stationFramesRef.current[r.index] = r.frames;
        }
        if (stationFramesRef.current[0]) setLoaded(true);
      } catch (err) { console.error('GIF decode error:', err); }
    }
    loadAll();
    return () => { cancelled = true; };
  }, [decodeGif]);

  // Animate GIF frames
  useEffect(() => {
    if (!loaded) return;
    const canvas = imgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frameIndex = 0, lastFrameTime = performance.now(), rafId, lastStationIdx = stationIndexRef.current;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    let lastWasStandby = false, standbyFrameIndex = 0;

    // Pre-create static noise ImageData for TV static effect
    const staticW = 320, staticH = 240;
    const staticImageData = ctx.createImageData(staticW, staticH);
    const staticPixels = staticImageData.data;
    const staticCanvas = document.createElement('canvas');
    staticCanvas.width = staticW;
    staticCanvas.height = staticH;
    const staticCtx2 = staticCanvas.getContext('2d');

    function drawFrame(now) {
      const W = canvas.width, H = canvas.height;

      // TV static noise overlay
      if (showStaticRef.current) {
        for (let i = 0; i < staticPixels.length; i += 4) {
          const v = Math.random() * 255 | 0;
          staticPixels[i] = v;
          staticPixels[i + 1] = v;
          staticPixels[i + 2] = v;
          staticPixels[i + 3] = 255;
        }
        staticCtx2.putImageData(staticImageData, 0, 0);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(staticCanvas, 0, 0, W, H);
        rafId = requestAnimationFrame(drawFrame);
        return;
      }

      const curStation = stationIndexRef.current;
      if (curStation !== lastStationIdx) { frameIndex = 0; lastFrameTime = now; lastStationIdx = curStation; }
      const useStandby = showStandbyRef.current && standbyFramesRef.current;
      const frames = useStandby ? standbyFramesRef.current : (stationFramesRef.current[curStation] || stationFramesRef.current[0]);
      if (!frames) { rafId = requestAnimationFrame(drawFrame); return; }
      if (useStandby !== lastWasStandby) { if (useStandby) standbyFrameIndex = 0; lastFrameTime = now; lastWasStandby = useStandby; }
      const idx = useStandby ? standbyFrameIndex : frameIndex;
      const frame = frames[idx];
      if (!frame) { rafId = requestAnimationFrame(drawFrame); return; }
      const elapsed = now - lastFrameTime;
      if (elapsed >= (frame.delay <= 0 ? 100 : frame.delay)) {
        if (useStandby) standbyFrameIndex = (standbyFrameIndex + 1) % frames.length;
        else frameIndex = (frameIndex + 1) % frames.length;
        lastFrameTime = now;
      }
      const current = frames[useStandby ? standbyFrameIndex : frameIndex];
      if (!current) { rafId = requestAnimationFrame(drawFrame); return; }
      if (tempCanvas.width !== current.width || tempCanvas.height !== current.height) { tempCanvas.width = current.width; tempCanvas.height = current.height; }
      tempCtx.putImageData(current.imageData, 0, 0);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);
      ctx.imageSmoothingEnabled = false;
      const scale = Math.max(W / current.width, H / current.height);
      const sw = current.width * scale, sh = current.height * scale;
      ctx.drawImage(tempCanvas, (W - sw) / 2, (H - sh) / 2, sw, sh);

      // --- Draw signal text onto canvas (inside CRT effect) ---
      const sigText = signalTextRef.current;
      if (sigText) {
        const sigElapsed = (now - signalStartTimeRef.current) / 1000;

        // Split into sentences, build up the paragraph over time
        const sentences = sigText.match(/[^.!?]+[.!?]?\s*/g) || [sigText];
        const CHARS_PER_SEC = 12;
        const HOLD_SEC = 2;

        // Build revealed text: all completed sentences + current one typing
        let timeAcc = 0;
        let revealed = '';

        for (let s = 0; s < sentences.length; s++) {
          const typeTime = sentences[s].length / CHARS_PER_SEC;
          if (sigElapsed < timeAcc + typeTime) {
            // Currently typing this sentence
            const chars = Math.floor((sigElapsed - timeAcc) * CHARS_PER_SEC);
            revealed += sentences[s].slice(0, chars);
            break;
          }
          // Sentence fully typed
          revealed += sentences[s];
          timeAcc += typeTime;
          // Hold pause before next sentence starts
          if (sigElapsed < timeAcc + HOLD_SEC) {
            break;
          }
          timeAcc += HOLD_SEC;
        }

        if (revealed.length > 0) {
          ctx.save();
          const fontSize = Math.max(14, Math.floor(W * 0.018));
          ctx.font = fontSize + 'px "IBM BIOS", "Lucida Console", monospace';
          ctx.textBaseline = 'top';

          // Word-wrap the full revealed paragraph
          const maxWidth = W * 0.7;
          const words = revealed.split(' ');
          const lines = [];
          let line = '';
          for (const word of words) {
            const test = line ? line + ' ' + word : word;
            if (ctx.measureText(test).width > maxWidth && line) {
              lines.push(line);
              line = word;
            } else {
              line = test;
            }
          }
          if (line) lines.push(line);

          const lineHeight = fontSize * 1.6;
          const totalH = lines.length * lineHeight;
          const startY = (H - totalH) / 2;
          const startX = W * 0.15;

          ctx.fillStyle = '#ffffff';
          for (let i = 0; i < lines.length; i++) {
            const y = startY + i * lineHeight;
            ctx.fillText(lines[i], startX, y);
          }
          ctx.restore();
        }
      }

      rafId = requestAnimationFrame(drawFrame);
    }
    rafId = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(rafId);
  }, [loaded]);

  // Resize
  useEffect(() => {
    function onResize() {
      const w = window.innerWidth, h = window.innerHeight;
      if (imgCanvasRef.current) { imgCanvasRef.current.width = w; imgCanvasRef.current.height = h; }
      if (glCanvasRef.current) { glCanvasRef.current.width = w; glCanvasRef.current.height = h; }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // WebGL CRT
  useEffect(() => {
    if (!loaded) return;
    const glCanvas = glCanvasRef.current;
    const sourceCanvas = imgCanvasRef.current;
    if (!glCanvas || !sourceCanvas) return;
    glCanvas.width = window.innerWidth;
    glCanvas.height = window.innerHeight;
    const gl = glCanvas.getContext('webgl', { alpha: false, antialias: false });
    if (!gl) return;
    const prog = createShaderProgram(gl);
    if (!prog) return;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_position');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uRes = gl.getUniformLocation(prog, 'u_resolution');
    const startTime = performance.now();
    let rafId;
    function render() {
      const t = (performance.now() - startTime) / 1000;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
      gl.viewport(0, 0, glCanvas.width, glCanvas.height);
      gl.uniform1f(uTime, t);
      gl.uniform2f(uRes, glCanvas.width, glCanvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      rafId = requestAnimationFrame(render);
    }
    render();
    return () => cancelAnimationFrame(rafId);
  }, [loaded]);

  // --- Render ---
  const station = STATIONS[stationIndex];
  const uiOpacity = controlsVisible ? 1 : 0;
  const volBars = 10;
  const litBars = Math.round(volume * volBars);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#000', overflow: 'hidden', cursor: controlsVisible ? 'default' : 'none' }}>
      {/* Hidden source canvas */}
      <canvas ref={imgCanvasRef} style={{ display: 'none' }} />

      {/* WebGL CRT output */}
      <canvas ref={glCanvasRef} style={{ display: 'block', width: '100vw', height: '100vh' }} />

      {/* ═══ TOP RIGHT — Info menu ═══ */}
      <div className="crt-ui-overlay" style={{
        position: 'absolute', top: 16, right: 16, zIndex: 20,
        opacity: controlsVisible ? 1 : 0,
        pointerEvents: controlsVisible ? 'auto' : 'none',
        fontFamily: "'IBM BIOS', monospace",
        fontSize: 10,
        color: 'rgba(255,255,255,0.5)',
        letterSpacing: 1,
      }}>
        <button
          className="crt-btn"
          onClick={() => setInfoOpen(o => !o)}
          style={{ fontSize: 10 }}
        >
          {infoOpen ? '[X]' : '[?]'}
        </button>

        {infoOpen && (
          <div className="crt-glow" style={{
            marginTop: 6, padding: '10px 12px',
            background: 'rgba(0,0,0,0.95)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.6)',
            fontSize: 9, lineHeight: '2.4',
            fontFamily: "'IBM BIOS', monospace",
            letterSpacing: 1,
          }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 4, letterSpacing: 2 }}>KEYS</div>
            <div>SPACE ....... play/mute</div>
            <div>{'<'} {'>'} ......... station</div>
            <div>^ v ......... volume</div>
            <div>RETURN ...... gif</div>
            <div>I ........... info</div>
            {DEV_MODE && <>
              <div style={{ color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>D ........... ad break</div>
              <div style={{ color: 'rgba(255,255,255,0.4)' }}>S ........... signal</div>
            </>}
          </div>
        )}
      </div>

      {/* ═══ BOTTOM LEFT — Controls ═══ */}
      <div className="crt-ui-overlay" style={{
        position: 'absolute', bottom: 12, left: 16, zIndex: 10,
        opacity: controlsVisible ? 1 : 0,
        pointerEvents: controlsVisible ? 'auto' : 'none',
        fontFamily: "'IBM BIOS', monospace",
      }}>
        {/* Station display */}
        <div className="crt-glow" style={{
          fontSize: 11, color: 'rgba(255,255,255,0.7)', letterSpacing: 2,
          marginBottom: 4,
        }}>
          <span style={{ fontFamily: stationFont, color: 'rgba(255,255,255,0.85)' }}>{station.name}</span>
          {' '}<span style={{ fontSize: 9, opacity: 0.6 }}>{station.freq}</span>
        </div>

        {/* Status line */}
        {radioStatus !== 'off' && (
          <div className={radioStatus === 'ad break' ? 'crt-glow-warm' : 'crt-glow'} style={{
            fontSize: 8, letterSpacing: 2, marginBottom: 8,
            color: radioStatus === 'ad break' ? 'rgba(255,255,255,0.6)' : radioStatus === 'on air' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
          }}>
            {radioStatus === 'on air' ? '>> ON AIR' : radioStatus === 'ad break' ? '## BREAK' : '.. TUNING'}
          </div>
        )}

        {/* Controls row — ASCII style */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Play / Mute */}
          <button
            className={`crt-btn ${radioPlaying && !radioMuted ? 'active' : ''}`}
            onClick={toggleRadio}
            style={{ fontSize: 10 }}
          >
            {!radioPlaying ? '|>' : radioMuted ? '|>' : '||'}
          </button>

          {/* Tune back */}
          <button className="crt-btn" onClick={() => changeStation(-1)} style={{ fontSize: 10 }}>
            {'<<'}
          </button>

          {/* Tune forward */}
          <button className="crt-btn" onClick={() => changeStation(1)} style={{ fontSize: 10 }}>
            {'>>'}
          </button>

          <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 10, margin: '0 2px' }}>|</span>

          {/* Volume bars — chunky blocks */}
          <div
            style={{ display: 'flex', alignItems: 'flex-end', height: 14, cursor: 'pointer', padding: '2px 0' }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              setVolume(Math.max(0, Math.min(1, x / rect.width)));
            }}
          >
            {Array.from({ length: volBars }, (_, i) => (
              <div
                key={i}
                className={`vol-bar ${i < litBars ? 'lit' : ''}`}
                style={{ height: 3 + (i * 1.1) }}
              />
            ))}
          </div>

          {/* Dev: force ad break */}
          {DEV_MODE && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 10, margin: '0 2px' }}>|</span>
              <button
                className="crt-btn"
                onClick={() => playAdBreak()}
                style={{ fontSize: 8, opacity: 0.3 }}
              >
                [AD]
              </button>
              <button
                className="crt-btn"
                onClick={() => triggerSignal()}
                style={{ fontSize: 8, opacity: 0.3 }}
              >
                [SIG]
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
